import { ReactNode, useCallback, useMemo, useState } from 'react';
import cn from 'classnames';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FCX, ID, Tooltip, useUuid } from '@datassential/platform-ui-lib';
import { getAxisValues } from './SocialLineChart.helpers';
import SocialLineChartCurves from './SocialLineChartCurves';
import './SocialLineChart.scss';

export type ChartValue = number | null;

export interface SocialLineChartColumn {
    id: ID;
    title: string;
}

export interface SocialLineChartSeries {
    id: ID;
    title: ReactNode;
    color: string;
    values: ChartValue[];
}

interface SocialLineChartProps {
    series: SocialLineChartSeries[];
    columns: SocialLineChartColumn[];
    renderValue: (value: ChartValue) => ReactNode;
    renderTooltipTitle?: (columnTitle: string) => ReactNode;
    activeSeriesId?: ID;
    emptyStateLabels?: number[];
    animationKey?: string | number;
    onColumnClick?: (columnIndex: number) => void;
    selectedColumnIndex?: number | null;
}

const SocialLineChart: FCX<SocialLineChartProps> = ({
    series,
    columns,
    activeSeriesId = null,
    renderValue,
    animationKey,
    renderTooltipTitle = (columnTitle) => columnTitle,
    emptyStateLabels,
    onColumnClick,
    selectedColumnIndex = null,
    className,
    testId,
    style,
}) => {
    const id = useUuid();
    const [activeColumnIndex, setActiveColumnIndex] = useState<number | null>(null);

    const allValues = useMemo(
        (): number[] => {
            return series
                .flatMap(s => s.values)
                .filter((v): v is number => v !== null);
        },
        [series]
    );

    const maxValue = useMemo(
        () => allValues.length ? Math.max(...allValues) : 0,
        [allValues]
    );

    const minValue = useMemo(
        () => allValues.length ? Math.min(...allValues, 0) : 0,
        [allValues]
    );

    const hasData = useMemo(() => allValues.length > 0, [allValues]);

    const labels = useMemo(
        () => hasData ? getAxisValues(maxValue, minValue) : (emptyStateLabels ?? []),
        [hasData, maxValue, minValue, emptyStateLabels]
    );

    const activeColumn = useMemo(
        () => activeColumnIndex !== null ? columns[activeColumnIndex] : null,
        [activeColumnIndex, columns]
    );

    const tooltipData = useMemo(
        () => {
            if (activeColumnIndex === null) return [];

            return series
                .map(s => ({
                    id: s.id,
                    title: s.title,
                    color: s.color,
                    value: s.values[activeColumnIndex] ?? null,
                }))
                .sort((a, b) => {
                    if (b.value === null) return -1;
                    if (a.value === null) return 1;
                    return b.value - a.value;
                });
        },
        [series, activeColumnIndex]
    );

    const getTooltipValue = useCallback(
        (value: ChartValue) => renderValue(value),
        [renderValue]
    );

    return (
        <div
            className={cn('SocialLineChart', className)}
            style={style}
            data-testid={testId}
        >
            <div className="SocialLineChart__viewport">
                <div className="SocialLineChart__y-axis">
                    {labels.map((label, index) => (
                        <div
                            key={label}
                            className="SocialLineChart__y-axis-label"
                            data-testid={`textSocialLineChartYAxisLabel_${index}`}
                        >
                            {renderValue(label)}
                        </div>
                    ))}
                </div>
                <div className="SocialLineChart__chart-area">
                    {hasData && (
                        <div className="SocialLineChart__curves">
                            <AutoSizer>
                                {({ width, height }: { width: number; height: number }) => (
                                    <SocialLineChartCurves
                                        key={animationKey}
                                        series={series}
                                        columnsCount={columns.length}
                                        labels={labels}
                                        height={height}
                                        width={width}
                                        activeSeriesId={activeSeriesId}
                                        activeColumnIndex={activeColumnIndex}
                                    />
                                )}
                            </AutoSizer>
                        </div>
                    )}
                    <div className={cn('SocialLineChart__columns', !hasData && 'is-empty')}>
                        {columns.map((col, index) => (
                            <div
                                key={col.id}
                                className="SocialLineChart__column"
                            >
                                {hasData && (
                                    <div
                                        className={cn(
                                            'SocialLineChart__column-inner',
                                            onColumnClick && 'is-clickable',
                                            selectedColumnIndex === index && 'is-selected',
                                        )}
                                        data-tooltip-id={id}
                                        onMouseEnter={() => setActiveColumnIndex(index)}
                                        onMouseLeave={() => setActiveColumnIndex(null)}
                                        onClick={() => onColumnClick?.(index)}
                                    />
                                )}
                                <div
                                    className="SocialLineChart__column-label"
                                    data-testid={`textSocialLineChartColumnLabel_${index}`}
                                >
                                    {col.title}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <Tooltip
                    id={id}
                    place="right"
                    float
                    style={{
                        animation: 'none',
                        transition: 'none',
                        zIndex: 10,
                    }}
                >
                    {activeColumn && (
                        <div className="SocialLineChart__tooltip">
                            <div
                                className="SocialLineChart__tooltip-title"
                                data-testid="textSocialLineChartTooltipTitle"
                            >
                                {renderTooltipTitle(activeColumn.title)}
                            </div>
                            {tooltipData.map((s, index) => (
                                <div
                                    key={s.id}
                                    className={cn(
                                        'SocialLineChart__tooltip-row',
                                        activeSeriesId !== null && activeSeriesId !== s.id && 'is-dimmed',
                                        activeSeriesId === s.id && 'is-active',
                                    )}
                                    data-testid={`blockSocialLineChartTooltipRow_${index}`}
                                >
                                    <div>{s.title}</div>
                                    <div
                                        className="SocialLineChart__tooltip-row-color"
                                        style={{ background: s.color }}
                                    />
                                    <div className="SocialLineChart__tooltip-row-value">
                                        {getTooltipValue(s.value)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Tooltip>
            </div>
        </div>
    );
};

export default SocialLineChart;
